import { useCallback, useState } from "react";
import { apiService } from "../services/apiClient";
import { apiUrl } from "../services/apiUrl";

/**
 * Drives the per-dashboard text-to-DAX onboarding wizard.
 *
 * Thin wrapper over the /api/onboarding/* endpoints (see backend/routes/
 * onboarding.py). Each call returns the parsed `data` and also throws on a
 * non-2xx so the wizard can show an inline error. `busy` is a per-action label
 * the UI uses to disable buttons / show spinners.
 */
export function useOnboarding() {
  const [busy, setBusy] = useState(null); // e.g. "introspect" | "dashboard-questions" | ...
  const [error, setError] = useState(null);

  const run = useCallback(async (label, fn) => {
    setBusy(label);
    setError(null);
    try {
      const res = await fn();
      const data = res?.data ?? res;
      if (data && data.success === false) {
        throw new Error(data.message || "Request failed");
      }
      return data;
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Request failed";
      setError(msg);
      throw err;
    } finally {
      setBusy(null);
    }
  }, []);

  const start = useCallback(
    (reportGuid) => run("start", () => apiService.post(apiUrl.onboardingStart(), { reportGuid })),
    [run]
  );
  const getState = useCallback(
    (id) => run("state", () => apiService.get(apiUrl.onboardingState(id))),
    [run]
  );
  const introspect = useCallback(
    (id) => run("introspect", () => apiService.post(apiUrl.onboardingIntrospect(id))),
    [run]
  );
  const saveTables = useCallback(
    (id, tables) => run("tables", () => apiService.post(apiUrl.onboardingTables(id), { tables })),
    [run]
  );
  const genDashboardQuestions = useCallback(
    (id) => run("dashboard-questions", () => apiService.post(apiUrl.onboardingGenDashboardQuestions(id))),
    [run]
  );
  const genSourceQuestions = useCallback(
    (id) => run("source-questions", () => apiService.post(apiUrl.onboardingGenSourceQuestions(id))),
    [run]
  );
  const genKpis = useCallback(
    (id) => run("kpis", () => apiService.post(apiUrl.onboardingGenKpis(id))),
    [run]
  );
  const genMetadata = useCallback(
    (id) => run("metadata", () => apiService.post(apiUrl.onboardingGenMetadata(id))),
    [run]
  );
  const generateMeasureDax = useCallback(
    (id, measure_name, previous_dax, previous_error) => run("gen-measure-dax", () =>
      apiService.post(apiUrl.onboardingGenMeasureDax(id), { measure_name, previous_dax, previous_error })),
    [run]
  );
  const generateMeasuresDaxBulk = useCallback(
    (id, measure_names) => run("gen-measures-bulk", () =>
      apiService.post(apiUrl.onboardingGenMeasuresDaxBulk(id), { measure_names })),
    [run]
  );
  const generateQuestionDax = useCallback(
    (id, question, scope, previous_dax, previous_error) => run("gen-question-dax", () =>
      apiService.post(apiUrl.onboardingGenQuestionDax(id), { question, scope, previous_dax, previous_error })),
    [run]
  );
  const verifyDax = useCallback(
    (id, dax) => run("verify", () => apiService.post(apiUrl.onboardingVerifyDax(id), { dax })),
    [run]
  );
  const verifyMeasure = useCallback(
    (id, measure_name, expression) => apiService.post(apiUrl.onboardingVerifyMeasure(id), { measure_name, expression }),
    []
  );
  const saveFeedback = useCallback(
    (id, payload) => run("feedback", () => apiService.post(apiUrl.onboardingFeedback(id), payload)),
    [run]
  );
  const finalize = useCallback(
    (id) => run("finalize", () => apiService.post(apiUrl.onboardingFinalize(id))),
    [run]
  );
  const chat = useCallback(
    (reportGuid, question) => run("chat", () => apiService.post(apiUrl.onboardingChat(), { reportGuid, question })),
    [run]
  );
  const genDomainContext = useCallback(
    (id) => run("gen-domain-context", () => apiService.post(apiUrl.onboardingGenDomainContext(id))),
    [run]
  );
  const saveDomainContext = useCallback(
    (id, context) => run("save-domain-context", () => apiService.post(apiUrl.onboardingSaveDomainContext(id), { context })),
    [run]
  );
  const prepopulate = useCallback(
    (id) => run("prepopulate", () => apiService.post(apiUrl.onboardingPrepopulate(id))),
    [run]
  );
  const deleteOnboarding = useCallback(
    (id) => run("delete", () => apiService.delete(apiUrl.onboardingDelete(id))),
    [run]
  );
  const reopenOnboarding = useCallback(
    (id) => run("reopen", () => apiService.patch(apiUrl.onboardingReopen(id))),
    [run]
  );
  const getStatusByGuid = useCallback(
    (reportGuid) => run("status-by-guid", () => apiService.get(apiUrl.onboardingStatusByGuid(reportGuid))),
    [run]
  );

  return {
    busy, error, setError,
    start, getState, introspect, saveTables,
    genDashboardQuestions, genSourceQuestions, genKpis, genMetadata,
    generateMeasureDax, generateMeasuresDaxBulk, generateQuestionDax, verifyDax, verifyMeasure,
    saveFeedback, finalize, chat, genDomainContext, saveDomainContext,
    prepopulate, deleteOnboarding, reopenOnboarding, getStatusByGuid,
  };
}

export default useOnboarding;
